import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import LoginFields from "@/components/LoginFields";
import { ArrowLeft, ArrowRight, LoaderCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Bem-vindo, ${u.name.split(" ")[0]}!`);
      navigate(u.role === "admin" || u.role === "coach" ? "/admin" : "/atleta");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha no login");
    } finally { setLoading(false); }
  };

  return (
    <main className="min-h-screen grid lg:grid-cols-2 gsx-gradient text-slate-100">
      <div className="hidden lg:block relative overflow-hidden border-r border-amber-400/15">
        <img src="/brand/gsx-team.jpeg"
          alt="Equipe do Grupo Santo de Xadrez" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#061426]/85 to-[#071A33]/60" />
        <div className="absolute bottom-12 left-10 right-10 max-w-md xl:left-16">
          <Logo size={72} />
          <p className="mt-6 text-xs font-semibold uppercase tracking-[0.18em] text-amber-300">Grupo Santo de Xadrez</p>
          <h2 className="text-white text-4xl font-semibold mt-3 leading-tight">Seu próximo movimento começa <span className="text-amber-400">aqui.</span></h2>
          <p className="text-slate-300 mt-2">Acesso individual para atletas, treinadores e administradores.</p>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-12 sm:p-12 lg:min-h-screen">
        <form onSubmit={submit} aria-busy={loading} className="w-full max-w-md space-y-6" data-testid="login-form">
          <div className="lg:hidden"><Logo /></div>
          <div>
            <span className="inline-block rounded-full border border-amber-400/25 bg-amber-400/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-amber-300">Área restrita GSX</span>
            <h1 className="mt-4 text-3xl sm:text-4xl text-white font-semibold">Bem-vindo de volta</h1>
            <p className="text-slate-400 text-sm mt-3 leading-relaxed">Entre com suas credenciais para acompanhar sua jornada no GSX.</p>
          </div>
          <LoginFields email username={email} onUsernameChange={setEmail} password={password} onPasswordChange={setPassword} loading={loading} usernameTestId="login-email-input" passwordTestId="login-password-input" />
          <Button type="submit" disabled={loading} data-testid="login-submit-btn"
            className="w-full rounded-xl bg-amber-500 text-[#071A33] hover:bg-amber-400 font-semibold h-12">
            {loading ? "Entrando…" : "Entrar"}
            {loading ? <LoaderCircle className="h-4 w-4 motion-safe:animate-spin" aria-hidden="true" /> : <ArrowRight className="h-4 w-4" aria-hidden="true" />}
          </Button>
          <Link to="/" className="flex items-center justify-center gap-2 rounded-lg text-sm text-slate-400 transition-colors hover:text-amber-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/50">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Voltar ao site
          </Link>
        </form>
      </div>
    </main>
  );
}
